<template>
  <header class="overview-header">
    <h1>Create Invoice</h1>
    <button class="add-button" @click="toggleForm">
      {{ showForm ? 'Close Form' : 'New Invoice' }}
    </button>
  </header>

  <!-- Create / Edit Form -->
  <section v-if="showForm" class="form-section">
    <h2>{{ isEditing ? 'Edit Invoice' : 'New Invoice' }}</h2>

    <form @submit.prevent="saveInvoice">
      <!-- Company Selection -->
      <div class="form-group">
        <label for="companyId">Company</label>
        <select id="companyId" v-model="form.companyId" @change="onCompanyChange" required>
          <option value="">Select company…</option>
          <option v-for="c in companies" :key="c.id" :value="c.id">
            {{ c.companyName }}
          </option>
        </select>
      </div>

      <!-- Company Address -->
      <div class="form-group">
        <label for="companyAddress">Company Address</label>
        <input id="companyAddress" v-model="form.companyAddress" type="text" readonly class="readonly-field" />
      </div>

      <!-- Property & Bill To -->
      <div class="grid grid-2">
        <div class="form-group">
          <label for="propertyId">Property</label>
          <select id="propertyId" v-model="form.propertyId" @change="onPropertyChange" required>
            <option value="">Select property…</option>
            <option v-for="p in properties" :key="p.id" :value="p.id">
              {{ p.propertyName }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="billTo">Bill To</label>
          <input id="billTo" v-model="form.billTo" type="text" readonly class="readonly-field" />
        </div>

      <!-- Bill To Email -->
      <div class="form-group">
        <label for="billToEmail">Bill To Email</label>
        <input id="billToEmail" v-model="form.billToEmail" type="email" readonly class="readonly-field" />
      </div>

      </div>

      <!-- Invoice Meta -->
      <div class="grid grid-3">
        <div class="form-group">
          <label for="invoiceNumber">Invoice #</label>
          <input id="invoiceNumber" v-model="form.invoiceNumber" type="text" required />
        </div>

        <div class="form-group">
          <label for="invoiceDate">Invoice Date</label>
          <input id="invoiceDate" v-model="form.invoiceDate" type="date" required />
        </div>

        <div class="form-group">
          <label for="customerId">Customer ID (optional)</label>
          <input id="customerId" v-model="form.customerId" type="text" />
        </div>
      </div>

      <!-- Period -->
      <div class="grid grid-2">
        <div class="form-group">
          <label for="periodStart">Invoice Period Start</label>
          <input id="periodStart" v-model="form.periodStart" type="date" required />
        </div>
        <div class="form-group">
          <label for="periodEnd">Invoice Period End</label>
          <input id="periodEnd" v-model="form.periodEnd" type="date" required />
        </div>
      </div>

      <!-- Department & Pay -->
      <div class="grid grid-2">
        <div class="form-group">
          <label for="departmentName">Department</label>
          <select id="departmentName" v-model="form.departmentName" @change="onDepartmentChange" required>
            <option value="">Select department…</option>
            <option v-for="d in departmentOptions" :key="d.name" :value="d.name">
              {{ d.name }} ({{ currency(d.hourlyRate) }}/hr)
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="payRate">Pay / Hour</label>
          <input id="payRate" v-model.number="form.payRate" type="number" min="0" step="0.01" required />
        </div>
      </div>

      <!-- Line Items -->
      <div class="lineitems-section">
        <div class="lineitems-header">
          <div class="header-left">
            <h3>Employees / Hours</h3>
            <span class="overtime-note">Overtime calculated at 1.5x regular rate</span>
          </div>
          <div class="header-buttons">
            <button type="button" class="clear-all-button" @click="clearAllLineItems">Clear All</button>
            <button type="button" class="add-dept-button" @click="addLineItem">Add Row</button>
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th style="width: 28%">Employee Name</th>
                <th style="width: 12%">Regular Hours</th>
                <th style="width: 12%">Overtime Hours</th>
                <th style="width: 12%">Rate</th>
                <th style="width: 12%">OT Rate</th>
                <th style="width: 12%">Line Total</th>
                <th style="width: 12%">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in form.lineItems" :key="idx">
                <td>
                  <div class="employee-search-wrapper">
                    <input v-model="row.searchTerm" type="text" placeholder="Search by name or employee ID..."
                      @input="onEmployeeSearch(idx, $event.target.value)"
                      @focus="row.showDropdown = row.searchTerm.length > 0" @blur="hideEmployeeDropdown(idx)"
                      required />
                    <div v-if="row.showDropdown" class="employee-dropdown">
                      <div v-for="emp in getFilteredEmployees(row.searchTerm)" :key="emp.id" class="employee-option"
                        @mousedown="selectEmployee(idx, emp)">
                        <div class="employee-name">{{ emp.firstName }} {{ emp.lastName }}</div>
                        <div class="employee-id">ID: {{ emp.employeeId }}</div>
                      </div>
                      <div v-if="getFilteredEmployees(row.searchTerm).length === 0" class="no-employees">
                        No employees found
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <input v-model.number="row.hours" type="number" min="0" step="0.01" @input="recalcRow(idx)"
                    required />
                </td>
                <td>
                  <input v-model.number="row.overtimeHours" type="number" min="0" step="0.01" @input="recalcRow(idx)"
                    placeholder="0" />
                </td>
                <td class="num">{{ currency(row.rate) }}</td>
                <td class="num">{{ currency(row.rate * 1.5) }}</td>
                <td class="num">{{ currency(row.lineTotal) }}</td>
                <td>
                  <button type="button" class="action-button delete" @click="removeLineItem(idx)"
                    :disabled="form.lineItems.length === 1">
                    Remove
                  </button>
                </td>
              </tr>
              <tr v-if="form.lineItems.length === 0">
                <td colspan="6" class="no-data">No rows yet.</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th>Total Regular</th>
                <td class="num"><strong>{{ totalHours.toFixed(2) }}</strong></td>
                <th>Total Overtime</th>
                <td class="num"><strong>{{ totalOvertimeHours.toFixed(2) }}</strong></td>
                <th>Grand Total</th>
                <td class="num"><strong>{{ currency(grandTotal) }}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div class="form-actions">
        <button type="submit" class="save-button">
          {{ isEditing ? 'Update Invoice' : 'Save Invoice' }}
        </button>
      </div>
    </form>
  </section>

  <!-- Invoices Table -->
  <section class="properties-table-section">
    <div class="invoice-table-controls">
      <div class="year-filter">
        <label for="invoiceYear">Year</label>
        <select id="invoiceYear" v-model="selectedYear">
          <option v-for="y in invoiceYears" :key="y" :value="y">{{ y }}</option>
          <option value="all">All years</option>
        </select>
      </div>

      <div class="year-filter">
        <label for="invoiceMonth">Month</label>
        <select id="invoiceMonth" v-model="selectedMonth">
          <option value="all">All months</option>
          <option v-for="(name, idx) in MONTH_NAMES" :key="name" :value="idx + 1">{{ name }}</option>
        </select>
      </div>

      <div class="year-filter">
        <label for="invoiceCompany">Company</label>
        <select id="invoiceCompany" v-model="selectedCompany">
          <option value="all">All companies</option>
          <option v-for="name in invoiceCompanies" :key="name" :value="name">{{ name }}</option>
        </select>
      </div>

      <div class="table-search">
        <i class="fas fa-search search-icon"></i>
        <input v-model="invoiceSearch" type="search" :placeholder="searchPlaceholder" aria-label="Search invoices" />
        <select v-model="invoiceSearchField" aria-label="Search in">
          <option value="default">Company / Invoice #</option>
          <option value="propertyName">Property</option>
          <option value="billTo">Bill To</option>
          <option value="departmentName">Department</option>
        </select>
      </div>

      <div class="archive-toggle">
        <label>
          <input type="checkbox" v-model="showArchivedInvoices" />
          Show Archived Invoices ({{ invoices.filter(inv => inv.archived).length }})
        </label>
      </div>
    </div>

    <div v-if="selectedInvoices.length > 0" class="bulk-actions">
      <span class="selection-count">{{ selectedInvoices.length }} invoice(s) selected</span>
      <button class="bulk-archive-button" @click="bulkArchiveInvoices">
        <i class="fas fa-archive"></i> Archive Selected
      </button>
      <button class="bulk-cancel-button" @click="clearSelection">Cancel</button>
    </div>

    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th style="width: 40px;">
              <input type="checkbox" @change="toggleSelectAll" :checked="allInvoicesSelected" />
            </th>
            <th>Invoice #</th>
            <th>Invoice Date</th>
            <th>Company</th>
            <th>Property</th>
            <th>Bill To</th>
            <th>Department</th>
            <th>Total Hours</th>
            <th>Grand Total</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="inv in pagedInvoices" :key="inv.id">
            <td>
              <input type="checkbox" :checked="selectedInvoices.includes(inv.id)" 
                @change="toggleInvoiceSelection(inv.id)" />
            </td>
            <td>{{ inv.invoiceNumber }}</td>
            <td>{{ formatDate(inv.invoiceDate) }}</td>
            <td>{{ inv.companyName || '—' }}</td>
            <td>{{ inv.propertyName }}</td>
            <td>{{ inv.billTo }}</td>
            <td>{{ inv.departmentName }}</td>
            <td class="num">{{ (inv.totalHours ?? 0).toFixed(2) }}</td>
            <td class="num">{{ currency(inv.grandTotal ?? 0) }}</td>
            <td>
              <button class="action-button edit" @click="startEdit(inv)">Edit</button>
              <button class="action-button print" @click="printInvoice(inv)">Print</button>
              <button class="action-button download" @click="downloadPDF(inv)">Download</button>
              <button class="action-button email" @click="emailInvoice(inv)" :disabled="!inv.billToEmail">Email</button>
              <button v-if="!inv.archived" class="action-button archive" @click="archiveInvoice(inv.id)">Archive</button>
              <button v-else class="action-button unarchive" @click="unarchiveInvoice(inv.id)">Unarchive</button>
              <button class="action-button delete" @click="deleteInvoice(inv.id)">Delete</button>
            </td>
          </tr>
          <tr v-if="filteredInvoices.length === 0">
            <td colspan="10" class="no-data">{{ emptyInvoicesMessage }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="filteredInvoices.length > 0" class="pagination">
      <span class="pagination-info">
        Showing {{ pageStart + 1 }}–{{ pageStart + pagedInvoices.length }} of {{ filteredInvoices.length }}
        · Total {{ currency(filteredTotal) }}
      </span>
      <div class="pagination-controls">
        <button type="button" :disabled="currentPage === 1" @click="currentPage = 1" aria-label="First page">«</button>
        <button type="button" :disabled="currentPage === 1" @click="currentPage--">Prev</button>
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
        <button type="button" :disabled="currentPage === totalPages" @click="currentPage++">Next</button>
        <button type="button" :disabled="currentPage === totalPages" @click="currentPage = totalPages" aria-label="Last page">»</button>
        <select v-model.number="pageSize" aria-label="Rows per page">
          <option :value="25">25 / page</option>
          <option :value="50">50 / page</option>
          <option :value="100">100 / page</option>
        </select>
      </div>
    </div>
  </section>

  <!-- Printable Invoice (Hidden on Screen) -->
  <div id="printable-invoice" v-if="printingInvoice">
    <div class="print-header-new">
      <div>
        <h1 class="print-company-name-new">{{ printingInvoice.companyName || 'RTH Cleaners' }}</h1>
        <p class="print-company-address-new">{{ printingInvoice.companyAddress || '3275 Parkway, Pigeon Forge TN 37863' }}</p>
        <p class="print-phone-new">Phone: (865) 352-8700</p>
      </div>
      <div class="print-invoice-section-new">
        <h2 class="print-invoice-title-new">INVOICE</h2>
        <p class="print-invoice-num-new">#{{ printingInvoice.invoiceNumber }}</p>
        <p class="print-invoice-date-new">Date: {{ formatDate(printingInvoice.invoiceDate) }}</p>
      </div>
    </div>
    <hr class="print-divider-new" />

    <div class="print-details-grid">
      <div class="print-bill-to">
        <h3>Bill To</h3>
        <p class="print-bill-name">{{ printingInvoice.billTo }}</p>
        <p>{{ printingInvoice.propertyName }}</p>
        <p>{{ printingInvoice.departmentName }}</p>
      </div>
      <div class="print-invoice-details">
        <h3>Invoice Details</h3>
        <p><strong>Period:</strong> {{ formatDate(printingInvoice.periodStart) }} - {{ formatDate(printingInvoice.periodEnd) }}</p>
        <p><strong>Rate:</strong> {{ currency(printingInvoice.payRate) }}/hr</p>
        <p v-if="printingInvoice.customerId"><strong>Customer ID:</strong> {{ printingInvoice.customerId }}</p>
      </div>
    </div>

    <table class="print-items-table">
      <thead>
        <tr>
          <th style="text-align: left;">Employee</th>
          <th style="text-align: right;">Regular Hrs</th>
          <th style="text-align: right;">Overtime Hrs</th>
          <th style="text-align: right;">Rate</th>
          <th style="text-align: right;">OT Rate</th>
          <th style="text-align: right;">Total</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, idx) in printingInvoice.lineItems" :key="idx">
          <td>{{ item.employeeName || item.employee || 'N/A' }}</td>
          <td style="text-align: right;">{{ (item.hours || 0).toFixed(2) }}</td>
          <td style="text-align: right;">{{ (item.overtimeHours || 0).toFixed(2) }}</td>
          <td style="text-align: right;">{{ currency(item.rate || 0) }}</td>
          <td style="text-align: right;">{{ currency((item.rate || 0) * 1.5) }}</td>
          <td style="text-align: right;">{{ currency(item.lineTotal || 0) }}</td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colspan="3" style="text-align: right; font-weight: bold;">Total Regular Hours:</td>
          <td style="text-align: right; font-weight: bold;">{{ (printingInvoice.totalHours || 0).toFixed(2) }}</td>
          <td></td>
        </tr>
        <tr>
          <td colspan="3" style="text-align: right; font-weight: bold;">Total Overtime Hours:</td>
          <td style="text-align: right; font-weight: bold;">{{ (printingInvoice.totalOvertimeHours || 0).toFixed(2) }}</td>
          <td></td>
        </tr>
        <tr>
          <td colspan="3" style="text-align: right; font-weight: bold;">GRAND TOTAL:</td>
          <td colspan="2" style="text-align: right; font-weight: bold;">{{ currency(printingInvoice.grandTotal || 0) }}</td>
        </tr>
      </tfoot>
    </table>

    <div class="print-thank-you">
      <p>Thank you for your business!</p>
    </div>
  </div>
</template>
<script setup>
import { notifyWarning, notifyError, confirmAction } from '@/services/notify'
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { onAuthStateChanged } from 'firebase/auth'
import {
  collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, serverTimestamp
} from 'firebase/firestore'
import { db, auth } from '@/firebase'
import Swal from 'sweetalert2'
import html2pdf from 'html2pdf.js'
import { getYear, getMonth, toTime, yearOptions } from '@/utils/dates'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']

/** ---------- Reactive state ---------- */
const router = useRouter()
const showForm = ref(false)
const isEditing = ref(false)
const editingId = ref(null)
const printingInvoice = ref(null)

const companies = ref([])
const properties = ref([])
const invoices = ref([])
const employees = ref([])
const selectedInvoices = ref([])
const showArchivedInvoices = ref(false)
const selectedYear = ref(new Date().getFullYear())
const invoiceSearch = ref('')
const invoiceSearchField = ref('default')
const selectedMonth = ref('all')
const selectedCompany = ref('all')
const currentPage = ref(1)
const pageSize = ref(25)
let unsubCompanies = null
let unsubProps = null
let unsubInvoices = null
let unsubEmployees = null

const form = reactive({
  companyId: '',
  companyName: '',
  companyAddress: '',
  propertyId: '',
  propertyName: '',
  billTo: '',
  billToEmail: '',
  invoiceNumber: genInvoiceNumber(),
  invoiceDate: todayISO(),
  customerId: '',
  periodStart: '',
  periodEnd: '',
  departmentName: '',
  payRate: 0,
  lineItems: [
    {
      employee: '',
      employeeId: '',
      employeeName: '',
      hours: 0,
      overtimeHours: 0,
      rate: 0,
      lineTotal: 0,
      searchTerm: '',
      showDropdown: false
    }
  ]
})

/** ---------- Derived ---------- */
const departmentOptions = computed(() => {
  const p = properties.value.find(x => x.id === form.propertyId)
  return (p?.departments ?? [])
})

const totalHours = computed(() =>
  form.lineItems.reduce((sum, r) => sum + (Number(r.hours) || 0), 0)
)

const totalOvertimeHours = computed(() =>
  form.lineItems.reduce((sum, r) => sum + (Number(r.overtimeHours) || 0), 0)
)

const grandTotal = computed(() =>
  form.lineItems.reduce((sum, r) => sum + (Number(r.lineTotal) || 0), 0)
)

const allInvoicesSelected = computed(() =>
  pagedInvoices.value.length > 0 && pagedInvoices.value.every(inv => selectedInvoices.value.includes(inv.id))
)

const invoiceYears = computed(() => yearOptions(invoices.value, 'invoiceDate'))

const invoiceCompanies = computed(() =>
  [...new Set(invoices.value.map(inv => inv.companyName).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
)

const searchPlaceholder = computed(() => ({
  default: 'Search by company or invoice #…',
  propertyName: 'Search by property…',
  billTo: 'Search by bill to…',
  departmentName: 'Search by department…'
})[invoiceSearchField.value])

function matchesInvoiceSearch(inv, term) {
  const fields = invoiceSearchField.value === 'default'
    ? [inv.companyName, inv.invoiceNumber]
    : [inv[invoiceSearchField.value]]
  return fields.some(f => String(f ?? '').toLowerCase().includes(term))
}

const filteredInvoices = computed(() => {
  const term = invoiceSearch.value.trim().toLowerCase()
  return invoices.value
    .filter(inv => showArchivedInvoices.value ? inv.archived === true : !inv.archived)
    .filter(inv => selectedYear.value === 'all' || getYear(inv.invoiceDate) === selectedYear.value)
    .filter(inv => selectedMonth.value === 'all' || getMonth(inv.invoiceDate) === selectedMonth.value)
    .filter(inv => selectedCompany.value === 'all' || inv.companyName === selectedCompany.value)
    .filter(inv => !term || matchesInvoiceSearch(inv, term))
    .sort((a, b) => toTime(b.invoiceDate) - toTime(a.invoiceDate))
})

const filteredTotal = computed(() =>
  filteredInvoices.value.reduce((sum, inv) => sum + (Number(inv.grandTotal) || 0), 0)
)

const totalPages = computed(() => Math.max(1, Math.ceil(filteredInvoices.value.length / pageSize.value)))
const pageStart = computed(() => (currentPage.value - 1) * pageSize.value)
const pagedInvoices = computed(() =>
  filteredInvoices.value.slice(pageStart.value, pageStart.value + pageSize.value)
)

// Stay on a valid page when rows are archived/deleted from under us
watch(totalPages, (pages) => {
  if (currentPage.value > pages) currentPage.value = pages
})

const emptyInvoicesMessage = computed(() => {
  if (invoiceSearch.value.trim() || selectedMonth.value !== 'all' || selectedCompany.value !== 'all') {
    return 'No invoices match your filters.'
  }
  const scope = selectedYear.value === 'all' ? '' : ` for ${selectedYear.value}`
  return showArchivedInvoices.value ? `No archived invoices${scope}.` : `No invoices${scope} yet.`
})

// On any filter change: back to page 1, and drop selections so bulk actions can't touch hidden rows
watch([selectedYear, selectedMonth, selectedCompany, invoiceSearch, invoiceSearchField, showArchivedInvoices, pageSize], () => {
  currentPage.value = 1
  selectedInvoices.value = []
})
watch(currentPage, () => {
  selectedInvoices.value = []
})

/** ---------- Lifecycle: Auth + snapshots ---------- */
onMounted(() => {
  const un = onAuthStateChanged(auth, (user) => {
    if (!user) {
      router.replace({ name: 'Login' })
    } else {
      // Companies feed
      unsubCompanies = onSnapshot(collection(db, 'companies'), (snap) => {
        companies.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      }, (error) => {
        console.error('Error loading companies:', error)
      })

      // Properties feed
      unsubProps = onSnapshot(collection(db, 'properties'), (snap) => {
        properties.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      }, (error) => {
        console.error('Error loading properties:', error)
      })

      // Invoices feed
      unsubInvoices = onSnapshot(collection(db, 'invoices'), (snap) => {
        invoices.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      }, (error) => {
        console.error('Error loading invoices:', error)
      })

      // Employees feed
      unsubEmployees = onSnapshot(collection(db, 'employees'), (snap) => {
        employees.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      }, (error) => {
        console.error('Error loading employees:', error)
      })
    }
    un()
  })
})

onUnmounted(() => {
  if (unsubCompanies) unsubCompanies()
  if (unsubProps) unsubProps()
  if (unsubInvoices) unsubInvoices()
  if (unsubEmployees) unsubEmployees()
})

/** ---------- Handlers ---------- */
function toggleForm() {
  if (isEditing.value) resetForm()
  showForm.value = !showForm.value
}

function onCompanyChange() {
  const c = companies.value.find(x => x.id === form.companyId)
  if (!c) return

  form.companyName = c.companyName || ''
  form.companyAddress = c.companyAddress || ''
}

function onPropertyChange() {
  const p = properties.value.find(x => x.id === form.propertyId)
  if (!p) return

  form.propertyName = p.propertyName || ''
  form.billTo = p.billTo || ''
  form.billToEmail = p.billToEmail || ''

  // If department list exists, pre-select first and set rate
  if (p.departments && p.departments.length > 0) {
    form.departmentName = p.departments[0].name
    form.payRate = Number(p.departments[0].hourlyRate) || 0
    // Also cascade rate into current rows that have 0 rate
    form.lineItems.forEach(r => {
      if (!r.rate || r.rate === 0) {
        r.rate = form.payRate
        recalcRowByRef(r)
      }
    })
  }
}

function onDepartmentChange() {
  const d = departmentOptions.value.find(x => x.name === form.departmentName)
  if (!d) return

  const rate = Number(d.hourlyRate) || 0
  form.payRate = rate

  // Optional behavior: sync each row's rate to department rate if rate equals previous payRate
  form.lineItems.forEach(r => {
    if (!r.rate || r.rate === 0) r.rate = rate
    recalcRowByRef(r)
  })
}

function addLineItem() {
  form.lineItems.push({
    employee: '',
    employeeId: '',
    employeeName: '',
    hours: 0,
    overtimeHours: 0,
    rate: form.payRate || 0,
    lineTotal: 0,
    searchTerm: '',
    showDropdown: false
  })
}

function removeLineItem(idx) {
  if (form.lineItems.length > 1) {
    form.lineItems.splice(idx, 1)
  }
}

async function clearAllLineItems() {
  const result = await Swal.fire({
    title: '<strong>🗑️ Clear All Line Items?</strong>',
    html: `
      <div style="text-align: center; margin: 1rem 0;">
        <p style="margin-bottom: 0.75rem; color: #4b5563; font-size: 15px;">This will remove all employee entries and reset the form.</p>
        <div style="display: inline-flex; align-items: center; padding: 8px 12px; background-color: #fef2f2; border-radius: 6px; border-left: 4px solid #dc2626;">
          <i class="fas fa-exclamation-triangle" style="color: #dc2626; margin-right: 8px;"></i>
          <span style="color: #dc2626; font-weight: 600; font-size: 13px;">This action cannot be undone</span>
        </div>
      </div>
    `,
    icon: 'warning',
    showCancelButton: true,
    focusCancel: true,
    confirmButtonText: '<i class="fas fa-trash-alt"></i> Clear All Items',
    cancelButtonText: '<i class="fas fa-shield-alt"></i> Keep Items',
    reverseButtons: true,
    customClass: {
      popup: 'custom-swal-popup',
      confirmButton: 'swal-confirm-clear',
      cancelButton: 'swal-cancel-btn',
    },
    buttonsStyling: false,
    showClass: {
      popup: 'animate__animated animate__zoomIn animate__faster'
    },
    hideClass: {
      popup: 'animate__animated animate__zoomOut animate__faster'
    }
  })

  if (result.isConfirmed) {
    // Add loading state
    Swal.fire({
      title: 'Clearing...',
      html: '<div class="custom-loader"></div>',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      customClass: {
        popup: 'custom-swal-popup'
      }
    })

    // Simulate brief delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500))

    form.lineItems = [{
      employee: '',
      employeeId: '',
      employeeName: '',
      hours: 0,
      overtimeHours: 0,
      rate: form.payRate || 0,
      lineTotal: 0,
      searchTerm: '',
      showDropdown: false
    }]

    // Show enhanced success toast
    Swal.fire({
      title: '✅ Cleared Successfully!',
      text: 'All line items have been reset to default.',
      icon: 'success',
      showConfirmButton: false,
      timer: 2000,
      toast: true,
      position: 'top-end',
      timerProgressBar: true,
      customClass: {
        popup: 'success-toast'
      }
    })
  }
}

function recalcRow(idx) {
  if (idx >= 0 && idx < form.lineItems.length) {
    const r = form.lineItems[idx]
    const regularPay = (Number(r.hours) || 0) * (Number(r.rate) || 0)
    const overtimePay = (Number(r.overtimeHours) || 0) * (Number(r.rate) || 0) * 1.5
    r.lineTotal = round2(regularPay + overtimePay)
  }
}

function recalcRowByRef(r) {
  if (r) {
    const regularPay = (Number(r.hours) || 0) * (Number(r.rate) || 0)
    const overtimePay = (Number(r.overtimeHours) || 0) * (Number(r.rate) || 0) * 1.5
    r.lineTotal = round2(regularPay + overtimePay)
  }
}

function getFilteredEmployees(searchTerm) {
  if (!searchTerm || searchTerm.length < 1) return []

  const term = searchTerm.toLowerCase()
  return employees.value.filter(emp => {
    const firstName = (emp.firstName || '').toLowerCase()
    const lastName = (emp.lastName || '').toLowerCase()
    const employeeId = (emp.employeeId || '').toLowerCase()
    const fullName = `${firstName} ${lastName}`.trim()

    return firstName.includes(term) ||
      lastName.includes(term) ||
      fullName.includes(term) ||
      employeeId.includes(term)
  }).slice(0, 10) // Limit to 10 results
}

function selectEmployee(rowIndex, employee) {
  const row = form.lineItems[rowIndex]
  if (!row) return

  row.employee = employee.id
  row.employeeId = employee.employeeId || ''
  row.employeeName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim()
  row.searchTerm = row.employeeName
  row.showDropdown = false
}

function onEmployeeSearch(rowIndex, value) {
  const row = form.lineItems[rowIndex]
  if (!row) return

  row.searchTerm = value
  row.showDropdown = value.length > 0

  // Clear selection if search term doesn't match current employee
  if (row.employeeName && !row.employeeName.toLowerCase().includes(value.toLowerCase())) {
    row.employee = ''
    row.employeeId = ''
    row.employeeName = ''
  }
}

function hideEmployeeDropdown(rowIndex) {
  setTimeout(() => {
    const row = form.lineItems[rowIndex]
    if (row) row.showDropdown = false
  }, 200) // Delay to allow click on dropdown item
}

function startEdit(inv) {
  if (!inv || !inv.id) return

  isEditing.value = true
  editingId.value = inv.id
  showForm.value = true

  form.companyId = inv.companyId || ''
  form.companyName = inv.companyName || ''
  form.companyAddress = inv.companyAddress || ''
  form.propertyId = inv.propertyId || ''
  form.propertyName = inv.propertyName || ''
  form.billTo = inv.billTo || ''
  form.billToEmail = inv.billToEmail || ''
  form.invoiceNumber = inv.invoiceNumber || ''
  form.invoiceDate = toISODate(inv.invoiceDate)
  form.customerId = inv.customerId || ''
  form.periodStart = toISODate(inv.periodStart)
  form.periodEnd = toISODate(inv.periodEnd)
  form.departmentName = inv.departmentName || ''
  form.payRate = Number(inv.payRate) || 0

  form.lineItems = (inv.lineItems && Array.isArray(inv.lineItems) ? inv.lineItems : []).map((x) => ({
    employee: x?.employee || '',
    employeeId: x?.employeeId || '',
    employeeName: x?.employeeName || x?.employee || '', // fallback to old employee field
    hours: Number(x?.hours) || 0,
    overtimeHours: Number(x?.overtimeHours) || 0,
    rate: Number(x?.rate) || 0,
    lineTotal: round2(Number(x?.lineTotal) || (Number(x?.hours) * Number(x?.rate) + Number(x?.overtimeHours) * Number(x?.rate) * 1.5)),
    searchTerm: x?.employeeName || x?.employee || '',
    showDropdown: false
  }))

  // Ensure at least one line item
  if (form.lineItems.length === 0) {
    form.lineItems = [{
      employee: '',
      employeeId: '',
      employeeName: '',
      hours: 0,
      overtimeHours: 0,
      rate: 0,
      lineTotal: 0,
      searchTerm: '',
      showDropdown: false
    }]
  }
}

async function saveInvoice() {
  // Enhanced validation
  if (!form.companyId || !form.propertyId || !form.invoiceNumber?.trim() || !form.invoiceDate ||
    !form.periodStart || !form.periodEnd || !form.departmentName) {
    notifyWarning('Please complete all required fields.')
    return
  }

  if (!form.lineItems || form.lineItems.length === 0) {
    notifyWarning('Please add at least one line item.')
    return
  }

  // Validate line items
  for (let i = 0; i < form.lineItems.length; i++) {
    const item = form.lineItems[i]
    if (!item.employee && !item.employeeName?.trim()) {
      notifyWarning(`Please select an employee for row ${i + 1}.`)
      return
    }
    if (!item.hours || item.hours <= 0) {
      notifyWarning(`Please enter valid hours for row ${i + 1}.`)
      return
    }
    if (!item.rate || item.rate <= 0) {
      notifyWarning(`Please enter a valid rate for row ${i + 1}.`)
      return
    }
  }

  // Ensure totals are current
  form.lineItems.forEach(recalcRowByRef)

  const payload = {
    companyId: form.companyId,
    companyName: form.companyName,
    companyAddress: form.companyAddress,
    propertyId: form.propertyId,
    propertyName: form.propertyName,
    billTo: form.billTo,
    billToEmail: form.billToEmail,
    invoiceNumber: form.invoiceNumber.trim(),
    invoiceDate: form.invoiceDate,
    customerId: form.customerId || '',
    periodStart: form.periodStart,
    periodEnd: form.periodEnd,
    departmentName: form.departmentName,
    payRate: Number(form.payRate) || 0,
    lineItems: form.lineItems.map(r => ({
      employee: r.employee,
      employeeId: r.employeeId || '',
      employeeName: r.employeeName || '',
      hours: Number(r.hours) || 0,
      overtimeHours: Number(r.overtimeHours) || 0,
      rate: Number(r.rate) || 0,
      lineTotal: round2(r.lineTotal || 0),
    })),
    totalHours: round2(totalHours.value),
    totalOvertimeHours: round2(totalOvertimeHours.value),
    grandTotal: round2(grandTotal.value),
    updatedAt: serverTimestamp(),
  }

  if (!isEditing.value) {
    payload.createdAt = serverTimestamp()
  }

  try {
    if (isEditing.value && editingId.value) {
      await updateDoc(doc(db, 'invoices', editingId.value), payload)
    } else {
      await addDoc(collection(db, 'invoices'), payload)
    }
    resetForm()
  } catch (e) {
    console.error('Error saving invoice:', e)
    notifyError('The invoice could not be saved. Please try again.')
  }
}

async function deleteInvoice(id) {
  if (!id) return
  if (!(await confirmAction({ title: 'Delete this invoice?', text: 'This cannot be undone.', confirmText: 'Delete', danger: true }))) return

  try {
    await deleteDoc(doc(db, 'invoices', id))
  } catch (e) {
    console.error('Error deleting invoice:', e)
    notifyError('The invoice could not be deleted. Please try again.')
  }
}

function toggleInvoiceSelection(invoiceId) {
  const idx = selectedInvoices.value.indexOf(invoiceId)
  if (idx > -1) {
    selectedInvoices.value.splice(idx, 1)
  } else {
    selectedInvoices.value.push(invoiceId)
  }
}

function toggleSelectAll() {
  if (allInvoicesSelected.value) {
    selectedInvoices.value = []
  } else {
    selectedInvoices.value = pagedInvoices.value.map(inv => inv.id)
  }
}

function clearSelection() {
  selectedInvoices.value = []
}

async function archiveInvoice(id) {
  if (!id) return
  const confirmed = await Swal.fire({
    title: 'Archive Invoice?',
    text: 'This invoice will be moved to archives.',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Archive',
    cancelButtonText: 'Cancel'
  })

  if (confirmed.isConfirmed) {
    try {
      await updateDoc(doc(db, 'invoices', id), { archived: true })
      Swal.fire('Archived!', 'Invoice has been archived.', 'success')
    } catch (e) {
      console.error('Error archiving invoice:', e)
      Swal.fire('Error', 'Failed to archive invoice.', 'error')
    }
  }
}

async function bulkArchiveInvoices() {
  if (selectedInvoices.value.length === 0) return

  const result = await Swal.fire({
    title: `Archive ${selectedInvoices.value.length} Invoice(s)?`,
    html: `<p>These invoices will be moved to archives.</p>`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Archive All',
    cancelButtonText: 'Cancel'
  })

  if (result.isConfirmed) {
    Swal.fire({
      title: 'Archiving...',
      html: '<div class="custom-loader"></div>',
      allowOutsideClick: false,
      showConfirmButton: false
    })

    try {
      const updates = selectedInvoices.value.map(id =>
        updateDoc(doc(db, 'invoices', id), { archived: true })
      )
      await Promise.all(updates)

      clearSelection()
      Swal.fire('Success!', `${selectedInvoices.value.length} invoice(s) archived.`, 'success')
    } catch (e) {
      console.error('Error archiving invoices:', e)
      Swal.fire('Error', 'Failed to archive some invoices.', 'error')
    }
  }
}

async function unarchiveInvoice(id) {
  if (!id) return
  const confirmed = await Swal.fire({
    title: 'Unarchive Invoice?',
    text: 'This invoice will be restored to active invoices.',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Unarchive',
    cancelButtonText: 'Cancel'
  })

  if (confirmed.isConfirmed) {
    try {
      await updateDoc(doc(db, 'invoices', id), { archived: false })
      Swal.fire('Restored!', 'Invoice has been restored.', 'success')
    } catch (e) {
      console.error('Error unarchiving invoice:', e)
      Swal.fire('Error', 'Failed to restore invoice.', 'error')
    }
  }
}

function printInvoice(invoice) {
  // Generate the same PDF HTML as downloadPDF but print instead of download
  const element = document.createElement('div')
  element.innerHTML = `
    <div style="padding: 20px; font-family: Arial, sans-serif; color: #333;">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px; padding-bottom: 0;">
        <div>
          <h1 style="margin: 0; color: #1f2937; font-size: 36px; font-weight: 900; line-height: 1.2;">${invoice.companyName || 'RTH Cleaners'}</h1>
          <p style="margin: 8px 0 5px 0; color: #666; font-size: 16px; font-weight: normal;">${invoice.companyAddress || '1169 Wears Valley Road Pigeon Forge, Tennesse 37863'}</p>
          <p style="margin: 5px 0; color: #666; font-size: 14px;">Phone: (865) 352-8700</p>
        </div>
        <div style="text-align: right;">
          <h2 style="margin: 0; color: #2563eb; font-size: 28px; font-weight: bold;">INVOICE</h2>
          <p style="margin: 5px 0; font-size: 14px;">${invoice.invoiceNumber}</p>
          <p style="margin: 5px 0; font-size: 14px;">Date: ${formatDate(invoice.invoiceDate)}</p>
        </div>
      </div>
      <hr style="border: none; border-top: 3px solid #2563eb; margin: 20px 0 20px 0;" />

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px;">
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px;">
          <h3 style="margin: 0 0 10px 0; color: #1f2937; font-size: 13px;">Bill To</h3>
          <p style="margin: 5px 0; font-size: 12px;">${invoice.propertyName}</p>
          <p style="margin: 5px 0; font-weight: bold; font-size: 12px;">${invoice.billTo}</p>
        </div>
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px;">
          <h3 style="margin: 0 0 10px 0; color: #1f2937; font-size: 13px;">Invoice Details</h3>
          <p style="margin: 5px 0; font-size: 12px;"><strong>Period:</strong> ${formatDate(invoice.periodStart)} - ${formatDate(invoice.periodEnd)}</p>
          ${invoice.customerId ? `<p style="margin: 5px 0; font-size: 12px;"><strong>Customer ID:</strong> ${invoice.customerId}</p>` : ''}
        </div>
      </div>

      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead>
          <tr style="background-color: #2563eb; color: white;">
            <th style="padding: 12px; text-align: left; border: 1px solid #ddd;">Employee</th>
            <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Regular Hrs</th>
            <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Overtime Hrs</th>
            <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Rate</th>
            <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">OT Rate</th>
            <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${(invoice.lineItems || []).map(item => `
            <tr>
              <td style="padding: 10px; border: 1px solid #eee;">${item.employeeName || item.employee || 'N/A'}</td>
              <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${(item.hours || 0).toFixed(2)}</td>
              <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${(item.overtimeHours || 0).toFixed(2)}</td>
              <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${currency(item.rate || 0)}</td>
              <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${currency((item.rate || 0) * 1.5)}</td>
              <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${currency(item.lineTotal || 0)}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr style="background-color: #f8f9fa; font-weight: bold;">
            <td colspan="3" style="padding: 12px; text-align: right; border: 1px solid #ddd;">Total Regular Hours:</td>
            <td style="padding: 12px; text-align: right; border: 1px solid #ddd;">${(invoice.totalHours || 0).toFixed(2)}</td>
            <td colspan="2" style="padding: 12px; border: 1px solid #ddd;"></td>
          </tr>
          <tr style="background-color: #f8f9fa; font-weight: bold;">
            <td colspan="3" style="padding: 12px; text-align: right; border: 1px solid #ddd;">Total Overtime Hours:</td>
            <td style="padding: 12px; text-align: right; border: 1px solid #ddd;">${(invoice.totalOvertimeHours || 0).toFixed(2)}</td>
            <td colspan="2" style="padding: 12px; border: 1px solid #ddd;"></td>
          </tr>
          <tr style="background-color: #e0e7ff; color: #1e40af; font-weight: bold; font-size: 1.1em;">
            <td colspan="4" style="padding: 12px; text-align: right; border: 1px solid #ddd;">GRAND TOTAL:</td>
            <td colspan="2" style="padding: 12px; text-align: right; border: 1px solid #ddd;">${currency(invoice.grandTotal || 0)}</td>
          </tr>
        </tfoot>
      </table>

      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 0.9em;">
        <p style="margin: 5px 0;">Thank you for your business!</p>
      </div>
    </div>
  `

  // Use html2pdf to generate PDF then print
  const opt = {
    margin: 10,
    filename: `Invoice_${invoice.invoiceNumber}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
  }

  // Generate PDF and open print dialog
  html2pdf().set(opt).from(element).toPdf().get('pdf').then((pdf) => {
    // Open print dialog for the generated PDF
    const pdfUrl = pdf.output('bloburi')
    const printWindow = window.open(pdfUrl)
    printWindow.onload = () => {
      printWindow.print()
    }
  })
}

function downloadPDF(invoice) {
  // Create a temporary container with invoice HTML
  // email pdf generation
  const element = document.createElement('div')
  element.innerHTML = `
    <div style="padding: 20px; font-family: Arial, sans-serif; color: #333;">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px; padding-bottom: 0;">
        <div>
          <h1 style="margin: 0; color: #1f2937; font-size: 36px; font-weight: 900; line-height: 1.2;">${invoice.companyName || 'RTH Cleaners'}</h1>
          <p style="margin: 8px 0 5px 0; color: #666; font-size: 16px; font-weight: normal;">${invoice.companyAddress || '1169 Wears Valley Road Pigeon Forge, Tennesse 37863'}</p>
          <p style="margin: 5px 0; color: #666; font-size: 14px;">Phone: (865) 352-8700</p>
        </div>
        <div style="text-align: right;">
          <h2 style="margin: 0; color: #2563eb; font-size: 28px; font-weight: bold;">INVOICE</h2>
          <p style="margin: 5px 0; font-size: 14px;">${invoice.invoiceNumber}</p>
          <p style="margin: 5px 0; font-size: 14px;">Date: ${formatDate(invoice.invoiceDate)}</p>
        </div>
      </div>
      <hr style="border: none; border-top: 3px solid #2563eb; margin: 20px 0 20px 0;" />

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px;">
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px;">
          <h3 style="margin: 0 0 10px 0; color: #1f2937; font-size: 13px;">Bill To</h3>
          <p style="margin: 5px 0; font-size: 12px;">${invoice.propertyName}</p>
          <p style="margin: 5px 0; font-weight: bold; font-size: 12px;">${invoice.billTo}</p>
        </div>
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px;">
          <h3 style="margin: 0 0 10px 0; color: #1f2937; font-size: 13px;">Invoice Details</h3>
          <p style="margin: 5px 0; font-size: 12px;"><strong>Period:</strong> ${formatDate(invoice.periodStart)} - ${formatDate(invoice.periodEnd)}</p>
          ${invoice.customerId ? `<p style="margin: 5px 0; font-size: 12px;"><strong>Customer ID:</strong> ${invoice.customerId}</p>` : ''}
        </div>
      </div>

      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead>
          <tr style="background-color: #2563eb; color: white;">
            <th style="padding: 12px; text-align: left; border: 1px solid #ddd;">Employee</th>
            <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Regular Hrs</th>
            <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Overtime Hrs</th>
            <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Rate</th>
            <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">OT Rate</th>
            <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${(invoice.lineItems || []).map(item => `
            <tr>
              <td style="padding: 10px; border: 1px solid #eee;">${item.employeeName || item.employee || 'N/A'}</td>
              <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${(item.hours || 0).toFixed(2)}</td>
              <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${(item.overtimeHours || 0).toFixed(2)}</td>
              <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${currency(item.rate || 0)}</td>
              <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${currency((item.rate || 0) * 1.5)}</td>
              <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${currency(item.lineTotal || 0)}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr style="background-color: #f8f9fa; font-weight: bold;">
            <td colspan="3" style="padding: 12px; text-align: right; border: 1px solid #ddd;">Total Regular Hours:</td>
            <td style="padding: 12px; text-align: right; border: 1px solid #ddd;">${(invoice.totalHours || 0).toFixed(2)}</td>
            <td colspan="2" style="padding: 12px; border: 1px solid #ddd;"></td>
          </tr>
          <tr style="background-color: #f8f9fa; font-weight: bold;">
            <td colspan="3" style="padding: 12px; text-align: right; border: 1px solid #ddd;">Total Overtime Hours:</td>
            <td style="padding: 12px; text-align: right; border: 1px solid #ddd;">${(invoice.totalOvertimeHours || 0).toFixed(2)}</td>
            <td colspan="2" style="padding: 12px; border: 1px solid #ddd;"></td>
          </tr>
          <tr style="background-color: #e0e7ff; color: #1e40af; font-weight: bold; font-size: 1.1em;">
            <td colspan="4" style="padding: 12px; text-align: right; border: 1px solid #ddd;">GRAND TOTAL:</td>
            <td colspan="2" style="padding: 12px; text-align: right; border: 1px solid #ddd;">${currency(invoice.grandTotal || 0)}</td>
          </tr>
        </tfoot>
      </table>

      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 0.9em;">
        <p style="margin: 5px 0;">Thank you for your business!</p>
      </div>
    </div>
  `

  // Generate PDF
  const opt = {
    margin: 10,
    filename: `Invoice_${invoice.invoiceNumber}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
  }

  html2pdf().set(opt).from(element).save()
}


async function emailInvoice(invoice) {
  if (!invoice.billToEmail) {
    await Swal.fire({
      icon: 'warning',
      title: 'No Email Address',
      text: 'This invoice does not have a billing email address.',
      confirmButtonColor: '#2563eb'
    })
    return
  }

  // Show loading
  Swal.fire({
    title: 'Sending Email...',
    html: 'Please wait while we send the invoice.',
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      Swal.showLoading()
    }
  })

  try {
    // Format line items for email (HTML table rows)
    let lineItemsRows = ''
    if (invoice.lineItems && invoice.lineItems.length > 0) {
      lineItemsRows = invoice.lineItems.map(item => {
        const empName = item.employeeName || item.employee || 'N/A'
        const regHours = (item.hours || 0).toFixed(2)
        const otHours = (item.overtimeHours || 0).toFixed(2)
        const rate = currency(item.rate || 0)
        const otRate = currency((item.rate || 0) * 1.5)
        const total = currency(item.lineTotal || 0)
        return `
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">${empName}</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${regHours}</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${otHours}</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${rate}</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${otRate}</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${total}</td>
          </tr>
        `
      }).join('')
    }

    // Format email message (HTML)
    const message = `
      <div style="padding: 20px; font-family: Arial, sans-serif; color: #333;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px; padding-bottom: 0;">
          <div>
            <h1 style="margin: 0; color: #1f2937; font-size: 28px; font-weight: 900; line-height: 1.2;">${invoice.companyName || 'RTH Cleaners'}</h1>
            <p style="margin: 8px 0 5px 0; color: #666; font-size: 14px; font-weight: normal;">${invoice.companyAddress || '1169 Wears Valley Road Pigeon Forge, Tennesse 37863'}</p>
            <p style="margin: 5px 0; color: #666; font-size: 12px;">Phone: (865) 352-8700</p>
          </div>
          <div style="text-align: right;">
            <h2 style="margin: 0; color: #2563eb; font-size: 24px; font-weight: bold;">INVOICE</h2>
            <p style="margin: 5px 0; font-size: 12px;">${invoice.invoiceNumber}</p>
            <p style="margin: 5px 0; font-size: 12px;">Date: ${formatDate(invoice.invoiceDate)}</p>
          </div>
        </div>
        <hr style="border: none; border-top: 3px solid #2563eb; margin: 20px 0 20px 0;" />

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px;">
          <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px;">
            <h3 style="margin: 0 0 10px 0; color: #1f2937; font-size: 13px;">Bill To</h3>
            <p style="margin: 5px 0; font-size: 12px;">${invoice.propertyName}</p>
            <p style="margin: 5px 0; font-weight: bold; font-size: 12px;">${invoice.billTo}</p>
          </div>
          <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px;">
            <h3 style="margin: 0 0 10px 0; color: #1f2937; font-size: 13px;">Invoice Details</h3>
            <p style="margin: 5px 0; font-size: 12px;"><strong>Period:</strong> ${formatDate(invoice.periodStart)} - ${formatDate(invoice.periodEnd)}</p>
            ${invoice.customerId ? `<p style="margin: 5px 0; font-size: 12px;"><strong>Customer ID:</strong> ${invoice.customerId}</p>` : ''}
          </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
          <thead>
            <tr style="background-color: #2563eb; color: white;">
              <th style="padding: 10px; text-align: left;">Employee</th>
              <th style="padding: 10px; text-align: right;">Regular Hrs</th>
              <th style="padding: 10px; text-align: right;">Overtime Hrs</th>
              <th style="padding: 10px; text-align: right;">Rate</th>
              <th style="padding: 10px; text-align: right;">OT Rate</th>
              <th style="padding: 10px; text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${lineItemsRows}
          </tbody>
          <tfoot>
             <tr style="font-weight: bold; background-color: #f8f9fa;">
              <td colspan="3" style="padding: 10px; text-align: right;">Total Regular Hours:</td>
              <td style="padding: 10px; text-align: right;">${(invoice.totalHours || 0).toFixed(2)}</td>
              <td colspan="2" style="padding: 10px;"></td>
            </tr>
             <tr style="font-weight: bold; background-color: #f8f9fa;">
              <td colspan="3" style="padding: 10px; text-align: right;">Total Overtime Hours:</td>
              <td style="padding: 10px; text-align: right;">${(invoice.totalOvertimeHours || 0).toFixed(2)}</td>
              <td colspan="2" style="padding: 10px;"></td>
            </tr>
             <tr style="font-weight: bold; background-color: #e0e7ff; color: #1e40af;">
              <td colspan="4" style="padding: 10px; text-align: right;">GRAND TOTAL:</td>
              <td colspan="2" style="padding: 10px; text-align: right;">${currency(invoice.grandTotal || 0)}</td>
            </tr>
          </tfoot>
        </table>

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 0.9em;">
          <p style="margin: 5px 0;">Thank you for your business!</p>
        </div>
      </div>
    `

    // Generate PDF as base64 for email attachment
    let pdfBase64 = null
    try {
      const element = document.createElement('div')
      element.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif; color: #333;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; border-bottom: 2px solid #2563eb; padding-bottom: 20px;">
            <div>
              <h1 style="margin: 0; color: #1f2937; font-size: 36px; font-weight: 900; line-height: 1.2;">${invoice.companyName || 'RTH Cleaners'}</h1>
             <p style="margin: 8px 0 5px 0; color: #666; font-size: 16px; font-weight: normal;">${invoice.companyAddress || '1169 Wears Valley Road Pigeon Forge, Tennesse 37863'}</p>
              <p style="margin: 5px 0; color: #666;">Phone: (865) 453-7400</p>
            </div>
            <div style="text-align: right;">
              <h2 style="margin: 0; color: #2563eb;">INVOICE</h2>
              <p style="margin: 5px 0; font-size: 14px;">${invoice.invoiceNumber}</p>
              <p style="margin: 5px 0; font-size: 14px;">Date: ${formatDate(invoice.invoiceDate)}</p>
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px;">
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px;">
              <h3 style="margin: 0 0 10px 0; color: #1f2937;">Bill To</h3>
               <p style="margin: 5px 0;">${invoice.propertyName}</p>
              <p style="margin: 5px 0; font-weight: bold;">${invoice.billTo}</p>      
            </div>
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px;">
              <h3 style="margin: 0 0 10px 0; color: #1f2937;">Invoice Details</h3>
              <p style="margin: 5px 0;"><strong>Period:</strong> ${formatDate(invoice.periodStart)} - ${formatDate(invoice.periodEnd)}</p>
              ${invoice.customerId ? `<p style="margin: 5px 0;"><strong>Customer ID:</strong> ${invoice.customerId}</p>` : ''}
            </div>
          </div>

          <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
            <thead>
              <tr style="background-color: #2563eb; color: white;">
                <th style="padding: 12px; text-align: left; border: 1px solid #ddd;">Employee</th>
                <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Regular Hrs</th>
                <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Overtime Hrs</th>
                <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Rate</th>
                <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">OT Rate</th>
                <th style="padding: 12px; text-align: right; border: 1px solid #ddd;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${(invoice.lineItems || []).map(item => `
                <tr>
                  <td style="padding: 10px; border: 1px solid #eee;">${item.employeeName || item.employee || 'N/A'}</td>
                  <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${(item.hours || 0).toFixed(2)}</td>
                  <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${(item.overtimeHours || 0).toFixed(2)}</td>
                  <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${currency(item.rate || 0)}</td>
                  <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${currency((item.rate || 0) * 1.5)}</td>
                  <td style="padding: 10px; border: 1px solid #eee; text-align: right;">${currency(item.lineTotal || 0)}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr style="background-color: #f8f9fa; font-weight: bold;">
                <td colspan="3" style="padding: 12px; text-align: right; border: 1px solid #ddd;">Total Regular Hours:</td>
                <td style="padding: 12px; text-align: right; border: 1px solid #ddd;">${(invoice.totalHours || 0).toFixed(2)}</td>
                <td colspan="2" style="padding: 12px; border: 1px solid #ddd;"></td>
              </tr>
              <tr style="background-color: #f8f9fa; font-weight: bold;">
                <td colspan="3" style="padding: 12px; text-align: right; border: 1px solid #ddd;">Total Overtime Hours:</td>
                <td style="padding: 12px; text-align: right; border: 1px solid #ddd;">${(invoice.totalOvertimeHours || 0).toFixed(2)}</td>
                <td colspan="2" style="padding: 12px; border: 1px solid #ddd;"></td>
              </tr>
              <tr style="background-color: #e0e7ff; color: #1e40af; font-weight: bold; font-size: 1.1em;">
                <td colspan="4" style="padding: 12px; text-align: right; border: 1px solid #ddd;">GRAND TOTAL:</td>
                <td colspan="2" style="padding: 12px; text-align: right; border: 1px solid #ddd;">${currency(invoice.grandTotal || 0)}</td>
              </tr>
            </tfoot>
          </table>

          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 0.9em;">
            <p style="margin: 5px 0;">Thank you for your business!</p>
          </div>
        </div>
      `

      const opt = {
        margin: 10,
        filename: `Invoice_${invoice.invoiceNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
      }

      // Generate PDF and convert to base64
      const pdf = await html2pdf().set(opt).from(element).outputPdf('blob')
      const reader = new FileReader()
      await new Promise((resolve) => {
        reader.onload = () => {
          pdfBase64 = reader.result.split(',')[1] // Extract base64 part
          resolve()
        }
        reader.readAsDataURL(pdf)
      })
    } catch (pdfError) {
      console.warn('Warning: Could not generate PDF attachment:', pdfError)
      // Continue without attachment if PDF generation fails
    }

    // Send email via Lambda
    const emailPayload = {
      recipient_email: invoice.billToEmail,
      subject: `Invoice ${invoice.invoiceNumber} from RTH Cleaners`,
      bcc_email: 'rthcleaners@rthgroupinc.com,keronoburrell@gmail.com',
      message: message
    }

    // Add PDF attachment if successfully generated
    if (pdfBase64) {
      emailPayload.attachment = {
        filename: `Invoice_${invoice.invoiceNumber}.pdf`,
        content: pdfBase64,
        encoding: 'base64'
      }
    }

    const res = await fetch(
      'https://7xkvku5mdsxqmdo2ifbhkagp3m0cbqum.lambda-url.us-east-2.on.aws/',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      }
    )

    // 502 Bad Gateway can happen if Lambda takes too long but email still sends
    // Treat 502 as success if it occurs during email sending
    if (!res.ok && res.status !== 502) {
      throw new Error(`Email API error (${res.status})`)
    }

    await Swal.fire({
      icon: 'success',
      title: 'Email Sent!',
      text: `Invoice has been sent to ${invoice.billToEmail}`,
      timer: 3000,
      showConfirmButton: false
    })
  } catch (error) {
    console.error('Error sending invoice email:', error)
    await Swal.fire({
      icon: 'error',
      title: 'Email Failed',
      text: 'Failed to send invoice email. Please try again.',
      confirmButtonColor: '#2563eb'
    })
  }
}
function resetForm() {
  form.companyId = ''
  form.companyName = ''
  form.companyAddress = ''
  form.propertyId = ''
  form.propertyName = ''
  form.billTo = ''
  form.billToEmail = ''
  form.invoiceNumber = genInvoiceNumber()
  form.invoiceDate = todayISO()
  form.customerId = ''
  form.periodStart = ''
  form.periodEnd = ''
  form.departmentName = ''
  form.payRate = 0
  form.lineItems = [{
    employee: '',
    employeeId: '',
    employeeName: '',
    hours: 0,
    overtimeHours: 0,
    rate: 0,
    lineTotal: 0,
    searchTerm: '',
    showDropdown: false
  }]
  isEditing.value = false
  editingId.value = null
  showForm.value = false
}

/** ---------- Utils ---------- */
function todayISO() {
  try {
    return new Date().toISOString().split('T')[0]
  } catch (e) {
    console.error('Error generating today\'s date:', e)
    return ''
  }
}

function toISODate(val) {
  try {
    if (!val) return ''
    if (val?.seconds) return new Date(val.seconds * 1000).toISOString().split('T')[0]
    const d = new Date(val)
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0]
  } catch (e) {
    console.error('Error converting date:', e)
    return ''
  }
}

function genInvoiceNumber() {
  try {
    const d = new Date()
    const y = String(d.getFullYear()).slice(-2)
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const rnd = Math.floor(1000 + Math.random() * 9000)
    return `FS${y}${m}${day}${rnd}`
  } catch (e) {
    console.error('Error generating invoice number:', e)
    return 'FS' + Date.now()
  }
}

function round2(n) {
  try {
    return Math.round((Number(n) || 0) * 100) / 100
  } catch (e) {
    console.error('Error rounding number:', e)
    return 0
  }
}

function currency(n) {
  try {
    return (Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD'
    })
  } catch (e) {
    console.error('Error formatting currency:', e)
    return '$0.00'
  }
}

function formatDate(val) {
  try {
    if (!val) return '—'
    
    // Handle Firestore timestamps
    if (val?.seconds) {
      const d = new Date(val.seconds * 1000)
      return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
    
    // Handle date strings (YYYY-MM-DD format from HTML date input)
    // Split and parse manually to avoid timezone conversion
    if (typeof val === 'string' && val.includes('-')) {
      const [year, month, day] = val.split('-')
      return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
    
    // Handle date objects
    const d = new Date(val)
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  } catch (e) {
    console.error('Error formatting date:', e)
    return '—'
  }
}
</script>

<style scoped>
/* Reuse your existing look & feel */
.overview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.overview-header h1 {
  font-size: 24px;
  color: #1f2937;
}

.add-button {
  background: #2563eb;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.add-button:hover {
  background: #1e40af;
}

.form-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
}

.form-section h2 {
  margin-bottom: 16px;
  font-size: 20px;
  color: #111827;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.form-group label {
  margin-bottom: 4px;
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.form-group input,
.form-group select {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.readonly-field {
  background: #f3f4f6 !important;
  color: #6b7280;
  cursor: not-allowed;
}

.grid {
  display: grid;
  gap: 12px;
}

.grid-2 {
  grid-template-columns: 1fr 1fr;
}

.grid-3 {
  grid-template-columns: 1fr 1fr 1fr;
}

@media (max-width: 820px) {

  .grid-2,
  .grid-3 {
    grid-template-columns: 1fr;
  }
}

.lineitems-section {
  margin-top: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  padding: 12px;
}

.lineitems-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-buttons {
  display: flex;
  gap: 8px;
}

.lineitems-header h3 {
  margin: 0;
  font-size: 16px;
  color: #374151;
}

.overtime-note {
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
}

.add-dept-button {
  background: #10b981;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.add-dept-button:hover {
  background: #059669;
}

.clear-all-button {
  background: #f59e0b;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.clear-all-button:hover {
  background: #d97706;
}

.table-wrap {
  overflow-x: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: #e5e7eb;
}

th,
td {
  padding: 12px 16px;
  text-align: left;
  color: #374151;
}

th {
  font-size: 12px;
  text-transform: uppercase;
  color: #6b7280;
}

tbody tr {
  border-bottom: 1px solid #e5e7eb;
}

tbody tr:hover {
  background: #f9fafb;
}

.num {
  text-align: right;
  white-space: nowrap;
}

.action-button {
  margin-right: 8px;
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color .2s ease;
  font-weight: 500;
}

.action-button.edit {
  background: #3b82f6;
  color: #fff;
}

.action-button.edit:hover {
  background: #2563eb;
}

.action-button.delete {
  background: #ef4444;
  color: #fff;
}

.action-button.delete:hover {
  background: #dc2626;
}

.action-button.archive {
  background: #0891b2;
  color: #fff;
}

.action-button.archive:hover {
  background: #0e7490;
}

.no-data {
  text-align: center;
  padding: 16px;
  color: #6b7280;
  font-style: italic;
}

.form-actions {
  margin-top: 16px;
}

.save-button {
  background: #10b981;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.save-button:hover {
  background: #059669;
}

/* Employee Search Styles */
.employee-search-wrapper {
  position: relative;
}

.employee-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.employee-option {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;
}

.employee-option:hover {
  background-color: #f3f4f6;
}

.employee-option:last-child {
  border-bottom: none;
}

.employee-name {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.employee-id {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.no-employees {
  padding: 8px 12px;
  color: #6b7280;
  font-style: italic;
  font-size: 12px;
  text-align: center;
}

/* SweetAlert2 Custom Styling */
:deep(.custom-swal-popup) {
  border-radius: 16px !important;
  padding: 2rem !important;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
  backdrop-filter: blur(10px) !important;
}

:deep(.swal2-title) {
  font-size: 1.5rem !important;
  font-weight: 600 !important;
  color: #1f2937 !important;
  margin-bottom: 1rem !important;
}

:deep(.swal2-html-container) {
  font-size: 15px !important;
  line-height: 1.6 !important;
  color: #4b5563 !important;
  margin-bottom: 1.5rem !important;
}

:deep(.swal2-actions) {
  gap: 12px !important;
  margin-top: 1.5rem !important;
}

:deep(.swal-confirm-clear) {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 4px 14px 0 rgba(220, 38, 38, 0.39) !important;
  position: relative !important;
  overflow: hidden !important;
}

:deep(.swal-confirm-clear:hover) {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%) !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(220, 38, 38, 0.5) !important;
}

:deep(.swal-confirm-clear:active) {
  transform: translateY(0) !important;
  transition: transform 0.1s !important;
}

:deep(.swal-confirm-clear::before) {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: -100% !important;
  width: 100% !important;
  height: 100% !important;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent) !important;
  transition: left 0.5s !important;
}

:deep(.swal-confirm-clear:hover::before) {
  left: 100% !important;
}

:deep(.swal-cancel-btn) {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 4px 14px 0 rgba(107, 114, 128, 0.39) !important;
  position: relative !important;
  overflow: hidden !important;
}

:deep(.swal-cancel-btn:hover) {
  background: linear-gradient(135deg, #4b5563 0%, #374151 100%) !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(107, 114, 128, 0.5) !important;
}

:deep(.swal-cancel-btn:active) {
  transform: translateY(0) !important;
  transition: transform 0.1s !important;
}

:deep(.swal-cancel-btn::before) {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: -100% !important;
  width: 100% !important;
  height: 100% !important;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent) !important;
  transition: left 0.5s !important;
}

:deep(.swal-cancel-btn:hover::before) {
  left: 100% !important;
}

/* Icon styling within buttons */
:deep(.swal-confirm-clear i),
:deep(.swal-cancel-btn i) {
  margin-right: 8px !important;
  font-size: 14px !important;
}

/* Warning icon enhancement */
:deep(.swal2-icon.swal2-warning) {
  border-color: #f59e0b !important;
  color: #f59e0b !important;
  font-size: 60px !important;
  animation: pulse-warning 2s infinite !important;
}

:deep(.swal2-icon.swal2-warning .swal2-icon-content) {
  color: #f59e0b !important;
  font-weight: 600 !important;
}

@keyframes pulse-warning {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }
}

/* Custom Loading Spinner */
.custom-loader {
  width: 40px;
  height: 40px;
  margin: 20px auto;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Success Toast Styling */
:deep(.success-toast) {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  padding: 1rem 1.5rem !important;
  box-shadow: 0 10px 25px 0 rgba(16, 185, 129, 0.4) !important;
  backdrop-filter: blur(10px) !important;
}

:deep(.success-toast .swal2-title) {
  color: white !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  margin: 0 !important;
}

:deep(.success-toast .swal2-content) {
  color: rgba(255, 255, 255, 0.9) !important;
  font-size: 14px !important;
  margin: 4px 0 0 0 !important;
}

/* Progress bar styling */
:deep(.swal2-timer-progress-bar) {
  background: rgba(255, 255, 255, 0.3) !important;
}

/* Animation classes for smooth transitions */
.animate__animated {
  animation-duration: 0.3s;
  animation-fill-mode: both;
}

.animate__zoomIn {
  animation-name: zoomIn;
}

.animate__zoomOut {
  animation-name: zoomOut;
}

.animate__faster {
  animation-duration: 0.2s;
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale3d(0.3, 0.3, 0.3);
  }

  50% {
    opacity: 1;
  }
}

@keyframes zoomOut {
  from {
    opacity: 1;
  }

  50% {
    opacity: 0;
    transform: scale3d(0.3, 0.3, 0.3);
  }

  to {
    opacity: 0;
  }
}

.action-button.print {
  background: #6366f1;
  color: white;
}

.action-button.print:hover {
  background: #4f46e5;
}

.action-button.download {
  background: #f59e0b;
  color: white;
}

.action-button.download:hover {
  background: #d97706;
}

.action-button.email {
  background: #10b981;
  color: white;
}

.action-button.email:hover:not(:disabled) {
  background: #059669;
}

.action-button.email:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  opacity: 0.6;
}

.action-button.unarchive {
  background: #8b5cf6;
  color: white;
}

.action-button.unarchive:hover {
  background: #7c3aed;
}

.invoice-table-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.year-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #374151;
}

.year-filter select,
.table-search select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
}

.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  color: #374151;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-controls button {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.pagination-controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-controls select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
}

.table-search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 320px;
}

.table-search .search-icon {
  position: absolute;
  left: 10px;
  color: #9ca3af;
  pointer-events: none;
}

.table-search input {
  flex: 1;
  min-width: 0;
  padding: 6px 10px 6px 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.archive-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.archive-toggle input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #0891b2;
}

.archive-toggle label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
  color: #374151;
}

.bulk-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f0f9ff;
  border: 2px solid #0284c7;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.selection-count {
  font-weight: 600;
  color: #0c4a6e;
}

.bulk-archive-button {
  background: #0891b2;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.bulk-archive-button:hover {
  background: #0e7490;
}

.bulk-cancel-button {
  background: #e5e7eb;
  color: #374151;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.bulk-cancel-button:hover {
  background: #d1d5db;
}

#printable-invoice {
  display: none;
}

@media print {
  .overview-header,
  .form-section,
  .properties-table-section,
  .admin-sidebar,
  .app-navbar,
  button {
    display: none !important;
  }

  #printable-invoice {
    display: block !important;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    background: white;
    padding: 20px;
    font-family: Arial, sans-serif;
    color: #333;
  }

  .print-header-new {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    margin-bottom: 15px !important;
    padding-bottom: 0 !important;
  }

  .print-company-name-new {
    margin: 0 !important;
    color: #1f2937 !important;
    font-size: 36px !important;
    font-weight: 900 !important;
    line-height: 1.2 !important;
  }

  .print-company-address-new {
    margin: 8px 0 5px 0 !important;
    color: #666 !important;
    font-size: 16px !important;
    font-weight: normal !important;
  }

  .print-phone-new {
    margin: 5px 0 !important;
    color: #666 !important;
    font-size: 14px !important;
  }

  .print-invoice-section-new {
    text-align: right !important;
  }

  .print-invoice-title-new {
    margin: 0 !important;
    color: #2563eb !important;
    font-size: 28px !important;
    font-weight: bold !important;
  }

  .print-invoice-num-new,
  .print-invoice-date-new {
    margin: 5px 0 !important;
    font-size: 14px !important;
  }

  .print-divider-new {
    border: none !important;
    border-top: 3px solid #2563eb !important;
    margin: 20px 0 20px 0 !important;
  }

  .print-details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;
  }

  .print-bill-to,
  .print-invoice-details {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
  }

  .print-bill-to h3,
  .print-invoice-details h3 {
    margin: 0 0 10px 0;
    color: #1f2937;
    font-size: 14px;
    font-weight: bold;
  }

  .print-bill-to p,
  .print-invoice-details p {
    margin: 5px 0;
    font-size: 12px;
  }

  .print-bill-name {
    font-weight: bold;
  }

  .print-items-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
  }

  .print-items-table thead {
    background-color: #2563eb;
    color: white;
  }

  .print-items-table th {
    padding: 12px;
    text-align: left;
    border: 1px solid #ddd;
    font-weight: bold;
    font-size: 12px;
  }

  .print-items-table td {
    padding: 10px 12px;
    border: 1px solid #eee;
    font-size: 12px;
  }

  .print-items-table tbody tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  .print-items-table tfoot tr:nth-child(1),
  .print-items-table tfoot tr:nth-child(2) {
    background-color: #f8f9fa;
    font-weight: bold;
  }

  .print-items-table tfoot tr:nth-child(3) {
    background-color: #e0e7ff;
    color: #1e40af;
    font-weight: bold;
    font-size: 13px;
  }

  .print-thank-you {
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #eee;
    color: #666;
    font-size: 12px;
  }

  .print-thank-you p {
    margin: 5px 0;
  }
}</style>
